import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContextMenu from '../Overlays/ContextMenu';
import { getChatComposerModelOptions } from '../../helpers/ai/aiConfigRuntimeHelpers';
import { getPreference, setPreference } from '../../helpers/preferencesHelpers';
import { screenSafePlacement } from '../../helpers/uiHelpers';
import Icon from '../Icon';

const CHAT_MODE_OPTIONS = new Set(['Explore', 'Write']);
const CHAT_MODE_PREFERENCE_KEY = 'chatComposerMode';
const getChatModelPreferenceKey = (mode) => `chatComposerModel_${String(mode || 'Explore').toLowerCase()}`;
const CHAT_MODEL_TIER_ORDER = ['free', 'budget', 'mid-tier', 'premium'];
const CHAT_MODEL_TIER_LABELS = {
  free: 'Free',
  budget: 'Budget',
  'mid-tier': 'Mid-tier',
  premium: 'Premium'
};
const SELECTION_CONTEXT_EVENT = 'ghost-selection-context-updated';
const TRANSACTIONAL_REWRITE_VERB_RE = /\b(rewrite|rephrase|reword|polish|tighten|shorten|improve|edit|fix)\b/i;
const TRANSACTIONAL_REWRITE_EXPANSION_RE = /\b(add|append|expand|extend|elaborate|include|introduce|invent|new info|new information|extra context|example|examples|research|fact check|facts?|citations?|source|sources)\b/i;
const TRANSACTIONAL_REWRITE_FRESH_SELECTION_MAX_AGE_MS = 90 * 1000;

const isTransactionalRewritePrompt = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized || normalized.length > 180) return false;
  if (!TRANSACTIONAL_REWRITE_VERB_RE.test(normalized)) return false;
  if (TRANSACTIONAL_REWRITE_EXPANSION_RE.test(normalized)) return false;
  return true;
};

const normalizeChatComposerMode = (value) => {
  const normalized = String(value || '').trim();
  return CHAT_MODE_OPTIONS.has(normalized) ? normalized : 'Explore';
};

const resolveChatComposerModel = (mode, candidate) => {
  const options = getChatComposerModelOptions({ mode: normalizeChatComposerMode(mode) });
  const normalizedCandidate = String(candidate || 'default').trim() || 'default';
  if (options.some(option => option.id === normalizedCandidate)) return normalizedCandidate;
  if (options.some(option => option.id === 'default')) return 'default';
  return options[0]?.id || 'default';
};

const normalizeTierKey = (value) => String(value || '').trim().toLowerCase();

const buildChatModelMenuItems = ({ modelOptions = [], selectedModelId, onSelect }) => {
  const options = Array.isArray(modelOptions) ? modelOptions : [];
  const defaultOption = options.find(option => option?.id === 'default' || option?.isDefault) || null;
  const defaultResolvedModelId = String(defaultOption?.resolvesTo || '').trim();
  const selectableOptions = options.filter(option => option && option.id !== 'default');
  const selectableTierKeys = Array.from(new Set(selectableOptions.map(option => normalizeTierKey(option?.tier)).filter(Boolean)));

  const orderedTierKeys = [
    ...CHAT_MODEL_TIER_ORDER.filter(tier => selectableTierKeys.includes(tier)),
    ...selectableTierKeys.filter(tier => !CHAT_MODEL_TIER_ORDER.includes(tier))
  ];

  const items = [];

  orderedTierKeys.forEach((tier) => {
    const tierOptions = selectableOptions.filter(option => normalizeTierKey(option?.tier) === tier);
    if (tierOptions.length === 0) return;

    items.push({
      label: CHAT_MODEL_TIER_LABELS[tier] || tier,
      className: 'contextTierHeader',
      isDisabled: true
    });
    items.push({ divider: true });

    tierOptions.forEach((option) => {
      const isResolvedDefault = defaultResolvedModelId && String(option.id) === defaultResolvedModelId;
      items.push({
        label: isResolvedDefault ? `${option.label} (Default)` : option.label,
        description: option.description || '',
        iconClass: 'check',
        isActive: selectedModelId === option.id || (selectedModelId === 'default' && isResolvedDefault),
        onClick: () => onSelect(isResolvedDefault ? 'default' : option.id)
      });
    });
  });

  return items;
};

const normalizeLinkedDocOptions = (options = []) => {
  const seen = new Set();
  return (Array.isArray(options) ? options : [])
    .map((entry) => {
      const numericId = Number(entry?.id);
      if (!Number.isFinite(numericId) || numericId <= 0) return null;
      const key = String(numericId);
      if (seen.has(key)) return null;
      seen.add(key);
      const label = String(entry?.label || '').trim() || `Doc ${numericId}`;
      return {
        id: numericId,
        label
      };
    })
    .filter(Boolean);
};

const buildProjectDocHref = (docId) => {
  const normalizedId = String(docId || '').trim();
  if (!normalizedId) return '';
  return `ghost://doc/${normalizedId}`;
};

export default function ChatComposer({
  docId,
  currentUserId = null,
  onSubmit,
  onOrchestrationChange,
  onFocusComposer,
  resolveSelectionReference = null,
  orchestrationEnabled = false,
  linkedDocOptions = [],
  maxLinkedDocs = 4,
  resolveLinkedDocOption = null,
  draggedId = null
}) {
  const [inputText, setInputText] = useState('');
  const [footerMenuOpen, setFooterMenuOpen] = useState(null);
  const [modelMenuPlacement, setModelMenuPlacement] = useState('right top');
  const [chatMode, setChatMode] = useState(() => {
    try {
      return normalizeChatComposerMode(getPreference(currentUserId, CHAT_MODE_PREFERENCE_KEY, 'Explore'));
    } catch {
      return 'Explore';
    }
  });
  const [chatModel, setChatModel] = useState(() => {
    try {
      const initialMode = normalizeChatComposerMode(getPreference(currentUserId, CHAT_MODE_PREFERENCE_KEY, 'Explore'));
      const storedModel = getPreference(currentUserId, getChatModelPreferenceKey(initialMode), 'default');
      return resolveChatComposerModel(initialMode, storedModel);
    } catch {
      return 'default';
    }
  });
  const [selectedLinkedDocs, setSelectedLinkedDocs] = useState([]);
  const [dismissedAutoDocIds, setDismissedAutoDocIds] = useState([]);
  const [selectedTemplateDoc, setSelectedTemplateDoc] = useState(null);
  const [selectedDocSelections, setSelectedDocSelections] = useState({});
  const [isDropActive, setIsDropActive] = useState(false);
  const [isTemplateDropActive, setIsTemplateDropActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const chatInputRef = useRef(null);
  const chatModeBtnRef = useRef(null);
  const chatModelBtnRef = useRef(null);
  const lastAutoSelectionKeyRef = useRef('');

  const normalizedLinkedDocOptions = useMemo(() => {
    return normalizeLinkedDocOptions(linkedDocOptions);
  }, [linkedDocOptions]);

  const safeMaxContextItems = Math.max(1, Math.min(4, Number(maxLinkedDocs || 4)));
  const maxSupportingDocs = Math.max(0, safeMaxContextItems - (selectedTemplateDoc ? 1 : 0));
  const dismissedAutoDocIdSet = useMemo(() => new Set(dismissedAutoDocIds), [dismissedAutoDocIds]);

  const autoLinkedDocs = useMemo(() => {
    return normalizedLinkedDocOptions
      .filter(entry => !dismissedAutoDocIdSet.has(Number(entry?.id)))
      .slice(0, safeMaxContextItems)
      .map(entry => ({ ...entry, source: 'auto' }));
  }, [dismissedAutoDocIdSet, normalizedLinkedDocOptions, safeMaxContextItems]);

  const normalizeLinkedDoc = useCallback((value) => {
    const numericId = Number(value?.id);
    if (!Number.isFinite(numericId) || numericId <= 0) return null;
    if (String(numericId) === String(docId || '')) return null;
    const label = String(value?.label || '').trim() || `Doc ${numericId}`;
    return { id: numericId, label };
  }, [docId]);

  const selectedLinkedDocIds = useMemo(() => {
    return selectedLinkedDocs.map(entry => entry.id);
  }, [selectedLinkedDocs]);

  const orchestrationSnapshot = useMemo(() => ({
    mode: chatMode,
    model: chatModel,
    linkedDocIds: orchestrationEnabled ? selectedLinkedDocIds : [],
    templateDocId: orchestrationEnabled ? Number(selectedTemplateDoc?.id || 0) || null : null,
    selectionContexts: orchestrationEnabled
      ? selectedLinkedDocIds
        .map((linkedDocId) => {
          const entry = selectedDocSelections[String(linkedDocId)] || null;
          if (!entry) return null;
          const selectedText = String(entry?.selectedText || '').trim();
          return {
            docId: linkedDocId,
            key: String(entry?.key || '').trim() || null,
            selectedText: selectedText || null,
            updatedAt: Number(entry?.updatedAt || 0) || null
          };
        })
        .filter(Boolean)
      : []
  }), [chatMode, chatModel, orchestrationEnabled, selectedDocSelections, selectedLinkedDocIds, selectedTemplateDoc?.id]);

  useEffect(() => {
    setInputText('');
    setFooterMenuOpen(null);
    setDismissedAutoDocIds([]);
    setSelectedTemplateDoc(null);
    setSelectedDocSelections({});
    setIsDropActive(false);
    setIsTemplateDropActive(false);
    lastAutoSelectionKeyRef.current = '';
  }, [docId]);

  useEffect(() => {
    const focusInput = () => {
      const node = chatInputRef.current;
      if (!node) return;
      try {
        node.focus({ preventScroll: true });
      } catch {
        node.focus();
      }
      const cursorPos = node.value.length;
      node.setSelectionRange(cursorPos, cursorPos);
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(focusInput));
      return;
    }

    const timer = setTimeout(focusInput, 0);
    return () => clearTimeout(timer);
  }, [docId]);

  useEffect(() => {
    setSelectedLinkedDocs((prev) => {
      const manualDocs = prev
        .filter(entry => entry?.source === 'manual')
        .map(entry => normalizeLinkedDoc(entry))
        .filter(Boolean)
        .map(entry => ({ ...entry, source: 'manual' }));

      const seen = new Set();
      const merged = [];

      for (const entry of autoLinkedDocs) {
        if (!entry) continue;
        const key = String(entry.id);
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(entry);
      }

      for (const entry of manualDocs) {
        if (!entry) continue;
        const key = String(entry.id);
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(entry);
      }

      return merged.slice(0, maxSupportingDocs);
    });
  }, [autoLinkedDocs, maxSupportingDocs, normalizeLinkedDoc]);

  useEffect(() => {
    setSelectedDocSelections((prev) => {
      if (!prev || typeof prev !== 'object') return {};
      const validIds = new Set(selectedLinkedDocs.map((entry) => String(entry?.id || '')));
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (!validIds.has(String(key))) return;
        next[key] = prev[key];
      });
      return next;
    });
  }, [selectedLinkedDocs]);

  useEffect(() => {
    let nextModel = 'default';
    try {
      const storedModel = getPreference(currentUserId, getChatModelPreferenceKey(chatMode), 'default');
      nextModel = resolveChatComposerModel(chatMode, storedModel);
    } catch {
      nextModel = resolveChatComposerModel(chatMode, 'default');
    }
    setChatModel(prev => (prev === nextModel ? prev : nextModel));
  }, [chatMode, currentUserId]);

  useEffect(() => {
    setPreference(currentUserId, CHAT_MODE_PREFERENCE_KEY, chatMode);
  }, [chatMode, currentUserId]);

  useEffect(() => {
    setPreference(currentUserId, getChatModelPreferenceKey(chatMode), chatModel);
  }, [chatMode, chatModel, currentUserId]);

  useEffect(() => {
    const input = chatInputRef.current;
    if (!input) return;
    input.style.height = '0px';
    const nextHeight = Math.max(40, Math.min(input.scrollHeight, 180));
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > 180 ? 'auto' : 'hidden';
  }, [inputText]);

  useEffect(() => {
    onOrchestrationChange?.(orchestrationSnapshot);
  }, [onOrchestrationChange, orchestrationSnapshot]);

  const buildSubmitOrchestration = useCallback((inputForSubmit = '') => {
    const base = orchestrationSnapshot && typeof orchestrationSnapshot === 'object'
      ? orchestrationSnapshot
      : {};

    const next = {
      ...base,
      linkedDocIds: Array.isArray(base.linkedDocIds) ? [...base.linkedDocIds] : [],
      selectionContexts: Array.isArray(base.selectionContexts) ? [...base.selectionContexts] : []
    };

    const transactionalRewritePrompt = isTransactionalRewritePrompt(inputForSubmit);
    const nowMs = Date.now();

    if (chatMode === 'Write') {
      // Drop stale selection contexts so write fallbacks cannot target old excerpts.
      next.selectionContexts = next.selectionContexts.filter((entry) => {
        const selectedText = String(entry?.selectedText || '').trim();
        if (!selectedText) return false;

        const updatedAt = Number(entry?.updatedAt || 0);
        if (!Number.isFinite(updatedAt) || updatedAt <= 0) {
          return !transactionalRewritePrompt;
        }

        const ageMs = Math.max(0, nowMs - updatedAt);
        if (transactionalRewritePrompt) {
          return ageMs <= TRANSACTIONAL_REWRITE_FRESH_SELECTION_MAX_AGE_MS;
        }
        return ageMs <= (15 * 60 * 1000);
      });
    }

    // Re-resolve selection right before submit in Write mode so stale chips do
    // not drive selection fallback against old prompt content.
    if (chatMode === 'Write' && typeof resolveSelectionReference === 'function') {
      const selectionRef = resolveSelectionReference() || null;
      const normalizedId = Number(selectionRef?.id || selectionRef?.docId || 0);
      const selectedText = String(selectionRef?.selectedText || '').trim();
      const selectionUpdatedAt = Number(selectionRef?.updatedAt || 0);

      if (Number.isFinite(normalizedId) && normalizedId > 0 && String(normalizedId) !== String(docId || '')) {
        if (!next.linkedDocIds.some((entry) => Number(entry) === normalizedId)) {
          next.linkedDocIds.push(normalizedId);
        }

        next.selectionContexts = next.selectionContexts.filter((entry) => {
          return Number(entry?.docId || 0) !== normalizedId;
        });

        if (selectedText) {
          next.selectionContexts.push({
            docId: normalizedId,
            key: String(selectionRef?.key || `${normalizedId}`).trim() || `${normalizedId}`,
            selectedText,
            updatedAt: Number.isFinite(selectionUpdatedAt) && selectionUpdatedAt > 0 ? selectionUpdatedAt : nowMs
          });
        }
      } else if (transactionalRewritePrompt) {
        // For short rewrite prompts we require a fresh selection in this turn.
        // If we do not have one, avoid reusing any previously cached excerpt.
        next.selectionContexts = [];
      }
    }

    return next;
  }, [chatMode, docId, orchestrationSnapshot, resolveSelectionReference]);

  const submitMessage = useCallback(async () => {
    const trimmed = inputText.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const submitOrchestration = buildSubmitOrchestration(trimmed);
      const result = await onSubmit?.({
        text: trimmed,
        orchestration: submitOrchestration
      });

      if (result !== false) {
        setInputText('');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [buildSubmitOrchestration, inputText, isSubmitting, onSubmit]);

  const selectedLinkedDocIdSet = useMemo(() => new Set(selectedLinkedDocIds), [selectedLinkedDocIds]);

  const addLinkedDoc = useCallback((value) => {
    const normalized = normalizeLinkedDoc(value);
    if (!normalized) return;
    setDismissedAutoDocIds((prev) => prev.filter(id => id !== normalized.id));
    setSelectedLinkedDocs((prev) => {
      const existing = prev.find(entry => entry.id === normalized.id);
      if (existing) {
        // Promoting an auto chip to manual keeps it across active-doc changes.
        if (existing.source === 'auto') {
          return prev.map(entry => (
            entry.id === normalized.id
              ? { ...entry, source: 'manual' }
              : entry
          ));
        }
        return prev;
      }
      if (prev.length >= maxSupportingDocs) return prev;
      return [...prev, { ...normalized, source: 'manual' }];
    });
  }, [maxSupportingDocs, normalizeLinkedDoc]);

  const applySelectionReference = useCallback((selectionRef) => {
    const normalizedId = Number(selectionRef?.id || selectionRef?.docId || 0);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;
    if (String(normalizedId) === String(docId || '')) return;

    const normalizedKey = String(selectionRef?.key || '').trim() || `${normalizedId}`;
    const selectedText = String(selectionRef?.selectedText || '').trim();
    const isCleared = Boolean(selectionRef?.cleared) || !selectedText;

    if (isCleared) {
      setSelectedDocSelections((prev) => {
        if (!prev || typeof prev !== 'object') return prev;
        if (!Object.prototype.hasOwnProperty.call(prev, String(normalizedId))) return prev;
        const next = { ...prev };
        delete next[String(normalizedId)];
        return next;
      });
      if (lastAutoSelectionKeyRef.current === normalizedKey) {
        lastAutoSelectionKeyRef.current = '';
      }
      return;
    }

    addLinkedDoc({
      id: normalizedId,
      label: String(selectionRef?.label || `Doc ${normalizedId}`).trim() || `Doc ${normalizedId}`
    });

    setSelectedDocSelections((prev) => ({
      ...(prev && typeof prev === 'object' ? prev : {}),
      [String(normalizedId)]: {
        key: normalizedKey,
        selectedText,
        updatedAt: Number(selectionRef?.updatedAt || Date.now()) || Date.now()
      }
    }));

    lastAutoSelectionKeyRef.current = normalizedKey;
  }, [addLinkedDoc, docId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleSelectionContextUpdate = (event) => {
      const detail = event?.detail;
      if (!detail || typeof detail !== 'object') return;
      applySelectionReference({
        id: detail.docId,
        label: detail.label,
        selectedText: detail.selectedText,
        key: detail.key,
        cleared: Boolean(detail.cleared)
      });
    };

    window.addEventListener(SELECTION_CONTEXT_EVENT, handleSelectionContextUpdate);
    return () => window.removeEventListener(SELECTION_CONTEXT_EVENT, handleSelectionContextUpdate);
  }, [applySelectionReference]);

  const removeLinkedDoc = useCallback((id) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    setDismissedAutoDocIds((prev) => (prev.includes(numericId) ? prev : [...prev, numericId]));
    setSelectedLinkedDocs(prev => prev.filter(entry => entry.id !== numericId));
    setSelectedDocSelections((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      if (!Object.prototype.hasOwnProperty.call(prev, String(numericId))) return prev;
      const next = { ...prev };
      delete next[String(numericId)];
      return next;
    });
  }, []);

  const resolveLinkedDocById = useCallback((id) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) return null;
    if (typeof resolveLinkedDocOption === 'function') {
      const resolved = resolveLinkedDocOption(numericId);
      const normalized = normalizeLinkedDoc(resolved);
      if (normalized) return normalized;
    }
    const fallback = normalizedLinkedDocOptions.find(entry => entry.id === numericId) || null;
    return normalizeLinkedDoc(fallback);
  }, [normalizeLinkedDoc, normalizedLinkedDocOptions, resolveLinkedDocOption]);

  const parseDroppedDocPayload = useCallback((event) => {
    let droppedId = null;
    let droppedKind = null;
    let droppedLabel = '';

    try {
      const raw = event?.dataTransfer?.getData?.('application/ghost') || '';
      if (raw) {
        const payload = JSON.parse(raw);
        droppedId = payload?.id ?? payload?.docId ?? payload?.doc_id ?? null;
        droppedKind = payload?.kind ?? payload?.type ?? payload?.docType ?? null;
        droppedLabel = String(payload?.name || payload?.label || payload?.title || '').trim();
      }
    } catch (err) {
      droppedId = null;
      droppedKind = null;
      droppedLabel = '';
    }

    if (!droppedId && typeof draggedId === 'string') {
      const parts = draggedId.split(':');
      if (parts.length === 2) {
        droppedKind = parts[0];
        droppedId = parts[1];
      }
    }

    if (!droppedId) {
      const plainTextId = String(event?.dataTransfer?.getData?.('text/plain') || '').trim();
      if (plainTextId) droppedId = plainTextId;
    }

    if (String(droppedKind || 'doc') !== 'doc') return null;
    const resolved = resolveLinkedDocById(droppedId);
    const fallback = normalizeLinkedDoc({
      id: droppedId,
      label: droppedLabel || `Doc ${String(droppedId || '').trim()}`
    });
    const normalized = resolved || fallback;
    if (!normalized) return null;
    return {
      ...normalized,
      href: buildProjectDocHref(normalized.id)
    };
  }, [draggedId, normalizeLinkedDoc, resolveLinkedDocById]);

  const handleContextDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDropActive(false);

    const resolved = parseDroppedDocPayload(event);
    if (!resolved) return;
    addLinkedDoc(resolved);
  }, [addLinkedDoc, parseDroppedDocPayload]);

  const handleTemplateDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsTemplateDropActive(false);

    if (!selectedTemplateDoc && selectedLinkedDocs.length >= safeMaxContextItems) return;

    const resolved = parseDroppedDocPayload(event);
    if (!resolved) return;
    setSelectedTemplateDoc(resolved);
  }, [parseDroppedDocPayload, safeMaxContextItems, selectedLinkedDocs.length, selectedTemplateDoc]);

  const insertDocLabelIntoInput = useCallback((entry) => {
    const label = String(entry?.label || `Doc ${entry?.id || ''}`).trim();
    if (!label) return;
    const decoratedLabel = `[${label}]`;

    setInputText((prev) => {
      const input = chatInputRef.current;
      const start = Number.isFinite(input?.selectionStart) ? input.selectionStart : prev.length;
      const end = Number.isFinite(input?.selectionEnd) ? input.selectionEnd : start;
      const before = prev.slice(0, start);
      const after = prev.slice(end);
      const prefix = before && !/\s$/.test(before) ? ' ' : '';
      const suffix = after && !/^\s/.test(after) ? ' ' : '';
      const insertion = `${prefix}${decoratedLabel}${suffix}`;
      const next = `${before}${insertion}${after}`;

      window.requestAnimationFrame(() => {
        const node = chatInputRef.current;
        if (!node) return;
        const cursor = before.length + insertion.length;
        node.focus();
        try { node.setSelectionRange(cursor, cursor); } catch (err) {}
      });

      return next;
    });
  }, []);

  const handleInputDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const resolved = parseDroppedDocPayload(event);
    if (!resolved) return;
    addLinkedDoc(resolved);
    insertDocLabelIntoInput(resolved);
  }, [addLinkedDoc, insertDocLabelIntoInput, parseDroppedDocPayload]);

  const modelOptions = getChatComposerModelOptions({ mode: chatMode });
  const selectedModelOption = modelOptions.find(option => option.id === chatModel) || modelOptions[0] || {
    id: 'default',
    label: 'Default',
    displayLabel: 'Default'
  };
  const totalContextItems = selectedLinkedDocIdSet.size + (selectedTemplateDoc ? 1 : 0);

  return (
    <div className="chatComposer" role="group" aria-label="Chat composer">
      <div
        className={`chatComposerContextRow ${isDropActive ? 'dropActive' : ''}`}
        aria-label="Composer context chips"
        onDragOver={(event) => {
          event.preventDefault();
          setIsDropActive(true);
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setIsDropActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          const relatedTarget = event.relatedTarget;
          if (relatedTarget && event.currentTarget.contains(relatedTarget)) return;
          setIsDropActive(false);
        }}
        onDrop={handleContextDrop}
      >
        <div className="chatComposerContextScroller">
          {selectedLinkedDocs.length === 0 && !selectedTemplateDoc && (
            <span className="chatComposerContextChip" aria-label="Context drop hint">Context doc</span>
          )}
          {selectedLinkedDocs.map(option => {
            const selectionEntry = selectedDocSelections[String(option.id)] || null;
            const hasSelection = Boolean(selectionEntry);
            const displayLabel = hasSelection ? `${option.label} (selection)` : option.label;
            return (
              <span
                key={option.id}
                className="chatComposerContextChip chip"
                title={displayLabel}
              >
                <span className="chatComposerChipLabel">{displayLabel}</span>
                <button
                  type="button"
                  className="chatComposerChipRemove"
                  aria-label={`Remove ${displayLabel} from context`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    removeLinkedDoc(option.id);
                  }}
                >
                  <Icon icon={'x'} className="icon small x" />
                </button>
              </span>
            );
          })}
          {selectedTemplateDoc ? (
            <span
              className={`chatComposerContextChip chatComposerTemplateChip chip ${isTemplateDropActive ? 'dropActive' : ''}`}
              title={selectedTemplateDoc.label}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsTemplateDropActive(true);
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsTemplateDropActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const relatedTarget = event.relatedTarget;
                if (relatedTarget && event.currentTarget.contains(relatedTarget)) return;
                setIsTemplateDropActive(false);
              }}
              onDrop={handleTemplateDrop}
            >
              <span className="chatComposerChipLabel">Template: {selectedTemplateDoc.label}</span>
              <button
                type="button"
                className="chatComposerChipRemove"
                aria-label={`Remove template ${selectedTemplateDoc.label}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setSelectedTemplateDoc(null);
                  setIsTemplateDropActive(false);
                }}
              >
                <Icon icon={'x'} className="icon small x" />
              </button>
            </span>
          ) : (
            <span
              className={`chatComposerContextChip chatComposerTemplateChip ${isTemplateDropActive ? 'dropActive' : ''}`}
              aria-label="Template drop hint"
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsTemplateDropActive(true);
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setIsTemplateDropActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const relatedTarget = event.relatedTarget;
                if (relatedTarget && event.currentTarget.contains(relatedTarget)) return;
                setIsTemplateDropActive(false);
              }}
              onDrop={handleTemplateDrop}
            >
              Template
            </span>
          )}
        </div>
        <span className="chatComposerContextCount">{`${totalContextItems}/${safeMaxContextItems}`}</span>
      </div>
      <textarea
        ref={chatInputRef}
        className="chatComposerInput"
        rows={1}
        placeholder="Let's explore some ideas..."
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDrop={handleInputDrop}
        onFocus={() => {
          onFocusComposer?.();
          const selectionRef = typeof resolveSelectionReference === 'function'
            ? resolveSelectionReference()
            : null;
          if (selectionRef?.key && selectionRef.key === lastAutoSelectionKeyRef.current) return;
          if (!selectionRef?.id) return;
          applySelectionReference(selectionRef);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (isSubmitting) return;
            void submitMessage();
          }
        }}
      />
      <div className="chatComposerFooter">
        <div className="chatComposerFooterLeft">
          <button
            ref={chatModeBtnRef}
            type="button"
            className="caretTextButton chatComposerSelect"
            data-context-placement="above"
            onClick={(e) => {
              e.stopPropagation();
              setFooterMenuOpen(prev => (prev === 'mode' ? null : 'mode'));
            }}
          >
            {chatMode}
            <Icon icon={footerMenuOpen === 'mode' ? 'caret-up-fill' : 'caret-down-fill'} className="icon caretIcon small" />
          </button>
          {footerMenuOpen === 'mode' && (
            <ContextMenu
              items={[
                {
                  label: 'Explore',
                  iconClass: 'chat-teardrop-dots',
                  isActive: chatMode === 'Explore',
                  onClick: () => {
                    setChatMode('Explore');
                    setFooterMenuOpen(null);
                  }
                },
                {
                  label: 'Write',
                  iconClass: 'chat-teardrop-text',
                  isActive: chatMode === 'Write',
                  onClick: () => {
                    setChatMode('Write');
                    setFooterMenuOpen(null);
                  }
                }
              ]}
              triggerRef={chatModeBtnRef}
              onClose={() => setFooterMenuOpen(null)}
              placement="above"
            />
          )}

          <button
            ref={chatModelBtnRef}
            type="button"
            className="caretTextButton chatComposerSelect"
            onClick={(e) => {
              e.stopPropagation();
              if (footerMenuOpen === 'model') {
                setFooterMenuOpen(null);
                return;
              }
              // Count selectable options + tier headers + dividers
              const selectableOptions = modelOptions.filter(option => option && option.id !== 'default');
              const tierCount = new Set(selectableOptions.map(option => normalizeTierKey(option?.tier)).filter(Boolean)).size;
              const itemCount = selectableOptions.length + (tierCount * 2);
              
              const placement = screenSafePlacement('right top', chatModelBtnRef.current, { itemCount, minMenuWidth: 420 });
              setModelMenuPlacement(placement);
              setFooterMenuOpen('model');
            }}
          >
            {selectedModelOption.displayLabel || selectedModelOption.label || 'Default'}
            <Icon icon={footerMenuOpen === 'model' ? 'caret-up-fill' : 'caret-down-fill'} className="icon caretIcon small" />
          </button>
          {footerMenuOpen === 'model' && (
            <ContextMenu
              items={buildChatModelMenuItems({
                modelOptions,
                selectedModelId: chatModel,
                onSelect: (modelId) => {
                  setChatModel(modelId);
                  setFooterMenuOpen(null);
                }
              })}
              triggerRef={chatModelBtnRef}
              onClose={() => setFooterMenuOpen(null)}
              placement={modelMenuPlacement}
              menuClass="chatModelMenuCtx"
            />
          )}
        </div>

        <button
          type="button"
          className="iconButton chatComposerSend"
          onClick={() => void submitMessage()}
          disabled={!inputText.trim() || isSubmitting}
          aria-label="Send message"
        >
          <Icon icon={'arrow-up'} className="icon arrow-up" />
        </button>
      </div>
    </div>
  );
}

